export interface DoctorAward {
  title: string
  organisation: string
  icon: string
}

export interface DoctorSkill {
  label: string
  percent: string
}

export interface Doctor {
  /** URL segment used by the `/doctors/:slug` route */
  slug: string
  name: string
  /** Short title shown on the listing card and detail header */
  specialty: string
  /** Star rating shown on the listing card */
  rating: number
  /** Listing card image */
  image: string
  /** Detail page portrait */
  detailImage: string
  qualification: string
  occupation: string
  experience: string
  certificates: string
  skills: string
  location: string
  phone: string
  email: string
  bio: string
  awards: DoctorAward[]
  skillLevels: DoctorSkill[]
}

const DETAIL_IMAGE = '/assets/images/doctor/single-doctor-1.jpg'

const HOSPITAL_LOCATION = 'Fast Aid Hospital, Chauddagram, Chittagong'
const HOSPITAL_PHONE = '09610009620'

const AWARDS: DoctorAward[] = [
  {
    title: 'BMDC Certified Specialist',
    organisation: 'Bangladesh Medical & Dental Council',
    icon: '/assets/images/doctor/awards-1.png',
  },
  {
    title: 'Excellence in Patient Care',
    organisation: 'Fast Aid Hospital, Chauddagram',
    icon: '/assets/images/doctor/awards-2.png',
  },
]

const SKILL_LEVELS: DoctorSkill[] = [
  { label: 'Patient Care', percent: '96%' },
  { label: 'Diagnosis', percent: '92%' },
  { label: 'Treatment', percent: '94%' },
]

type DoctorInput = Omit<Doctor, 'detailImage' | 'awards' | 'skillLevels'>

const doctorProfiles: DoctorInput[] = [
  {
    slug: 'abdul-rahman',
    name: 'Dr. Abdul Rahman',
    specialty: 'Cardiology',
    rating: 4.9,
    image: '/assets/images/doctor/doctor-1.jpg',
    qualification: 'MBBS, MD (Cardiology), FCPS',
    occupation: 'Senior Consultant, Cardiology',
    experience: '18 Years',
    certificates: 'MBBS, MD (Cardiology), FCPS',
    skills: 'Chest-pain evaluation, ECG interpretation and hypertension management',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.abdul@fastaid.com.bd',
    bio: 'Dr. Abdul Rahman is a senior cardiologist at Fast Aid Hospital with more than 18 years of experience in heart care. He specialises in the early detection of heart disease, blood-pressure control and long-term follow-up after cardiac events, and is known for explaining complex conditions in simple language.',
  },
  {
    slug: 'fatima-khatun',
    name: 'Dr. Fatima Khatun',
    specialty: 'Neurology',
    rating: 4.8,
    image: '/assets/images/doctor/doctor-2.jpg',
    qualification: 'MBBS, MD (Neurology), MRCP',
    occupation: 'Consultant, Neurology',
    experience: '14 Years',
    certificates: 'MBBS, MD (Neurology), MRCP (UK)',
    skills: 'Stroke prevention, headache and seizure management',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.fatima@fastaid.com.bd',
    bio: 'Dr. Fatima Khatun is a consultant neurologist who treats stroke, migraine, epilepsy, dizziness and nerve-related problems. She focuses on accurate diagnosis and patient education so families understand warning signs and follow treatment plans with confidence.',
  },
  {
    slug: 'kamal-hossain',
    name: 'Dr. Kamal Hossain',
    specialty: 'Orthopedics',
    rating: 4.8,
    image: '/assets/images/doctor/doctor-3.jpg',
    qualification: 'MBBS, MS (Orthopedics)',
    occupation: 'Senior Consultant, Orthopedics & Trauma',
    experience: '22 Years',
    certificates: 'MBBS, MS (Orthopedics)',
    skills: 'Fracture management, joint pain and sports-injury care',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.kamal@fastaid.com.bd',
    bio: 'Dr. Kamal Hossain leads the orthopedics and trauma service at Fast Aid Hospital. With over two decades of experience, he manages fractures, dislocations and joint problems, and works closely with the emergency team to treat accident patients safely and promptly.',
  },
  {
    slug: 'nasrin-akter',
    name: 'Dr. Nasrin Akter',
    specialty: 'Pediatrics',
    rating: 4.8,
    image: '/assets/images/doctor/doctor-4.jpg',
    qualification: 'MBBS, MD (Pediatrics)',
    occupation: 'Consultant, Pediatrics & Child Health',
    experience: '10 Years',
    certificates: 'MBBS, MD (Pediatrics)',
    skills: 'Newborn care, childhood infection and nutrition counselling',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.nasrin@fastaid.com.bd',
    bio: 'Dr. Nasrin Akter is a pediatrician dedicated to the health of newborns, children and adolescents. She treats fever, diarrhoea, respiratory infection and growth concerns, and gives parents practical, reassuring guidance on nutrition, immunisation and home care.',
  },
  {
    slug: 'rashed-khan',
    name: 'Dr. Rashed Khan',
    specialty: 'General Medicine',
    rating: 4.9,
    image: '/assets/images/doctor/doctor-5.jpg',
    qualification: 'MBBS, FCPS (Medicine)',
    occupation: 'Senior Consultant, General Medicine',
    experience: '16 Years',
    certificates: 'MBBS, FCPS (Medicine)',
    skills: 'Diabetes, hypertension and general adult medicine',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.rashed@fastaid.com.bd',
    bio: 'Dr. Rashed Khan is a senior physician in the outdoor department at Fast Aid Hospital. He manages fever, infection, diabetes, high blood pressure and other common adult conditions, and is valued for his thorough examinations and clear treatment advice.',
  },
  {
    slug: 'sultana-begum',
    name: 'Dr. Sultana Begum',
    specialty: 'Dermatology',
    rating: 4.7,
    image: '/assets/images/doctor/doctor-6.jpg',
    qualification: 'MBBS, MD (Dermatology)',
    occupation: 'Consultant, Dermatology & Skin Care',
    experience: '12 Years',
    certificates: 'MBBS, MD (Dermatology)',
    skills: 'Skin allergy, eczema, acne and infection treatment',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.sultana@fastaid.com.bd',
    bio: 'Dr. Sultana Begum treats a wide range of skin conditions including allergy, eczema, acne, fungal infection and hair problems. She combines medical treatment with practical skincare advice suited to the Bangladeshi climate.',
  },
  {
    slug: 'hasan-mahmud',
    name: 'Dr. Hasan Mahmud',
    specialty: 'Emergency Medicine',
    rating: 4.9,
    image: '/assets/images/doctor/doctor-7.jpg',
    qualification: 'MBBS, MD (Emergency Medicine)',
    occupation: 'Head of Emergency Department',
    experience: '20 Years',
    certificates: 'MBBS, MD (Emergency Medicine)',
    skills: 'Trauma resuscitation, critical care and emergency stabilisation',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.hasan@fastaid.com.bd',
    bio: 'Dr. Hasan Mahmud heads the 24/7 emergency department at Fast Aid Hospital. He is experienced in trauma resuscitation, critical-care stabilisation and the rapid assessment of life-threatening conditions, and leads the hospital emergency response team.',
  },
  {
    slug: 'ayesha-siddiqa',
    name: 'Dr. Ayesha Siddiqa',
    specialty: 'Gynecology',
    rating: 4.8,
    image: '/assets/images/doctor/doctor-8.jpg',
    qualification: 'MBBS, MD (Gynecology), MRCOG',
    occupation: 'Senior Consultant, Gynecology & Obstetrics',
    experience: '15 Years',
    certificates: 'MBBS, MD (Gynecology), MRCOG',
    skills: 'Antenatal care, women’s health and safe delivery support',
    location: HOSPITAL_LOCATION,
    phone: HOSPITAL_PHONE,
    email: 'dr.ayesha@fastaid.com.bd',
    bio: 'Dr. Ayesha Siddiqa provides respectful, private care for women at every stage of life. She manages antenatal and postnatal care, menstrual problems and other gynecological concerns, and supports safe delivery and referral when specialist facilities are needed.',
  },
]

export const doctors: Doctor[] = doctorProfiles.map((profile) => ({
  ...profile,
  detailImage: DETAIL_IMAGE,
  awards: AWARDS,
  skillLevels: SKILL_LEVELS,
}))

export function getDoctor(slug: string | undefined): Doctor | undefined {
  if (!slug) return undefined
  return doctors.find((doctor) => doctor.slug === slug)
}
